import React from 'react';
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Typography,
  alpha,
  Checkbox,
  Tooltip,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';
import ProductSelect from '../../features/invoices/components/ProductSelect';

/**
 * DocumentProductsTable Component
 * Displays product table with add, edit quantity/price, and remove functionality
 * Reusable across Delivery Notes, Sale Orders, and Invoices
 */
const DocumentProductsTable = ({
  products = [],
  onProductAdd,
  onProductRemove,
  onQuantityChange,
  onPriceChange,
  onPriceBlur,
  onTotalInclChange,
  onAddProductClick,
  loadProductOptions,
  vatPercentage,
  isTvaIncluded = true,
  showVatColumn = true,
  showTotalExclColumn = true,
  showTotalInclColumn = true,
  convertToInvoiceMode = false,
  selectedLineItems = [],
  onLineItemToggle,
  onLineItemSelectAll,
  showProfit = false,
  onDiscountToggle,
  showDiscountColumn = false,
  showAddProductRow = true,
  onCustomProductOptionSelect,
  externalPendingProduct,
  onExternalPendingConsumed,
  showSalePriceColumn = false,
  showCostPriceColumn = false,
  onProductEdit,
  onSerialNumberClick,
  productSearchKey,
}) => {
  const { t } = useTranslation();
  const [pendingProduct, setPendingProduct] = React.useState(null);
  const productSelectRef = React.useRef(null);
  const [pendingQuantity, setPendingQuantity] = React.useState(1);
  const [pendingPrice, setPendingPrice] = React.useState('');
  const [pendingError, setPendingError] = React.useState('');
  const [selectKey, setSelectKey] = React.useState(0);
  
  // Track original values to prevent unnecessary auto-save calls
  const originalValuesRef = React.useRef({});

  // Consume external pending product (e.g. newly created product from dialog)
  React.useEffect(() => {
    if (externalPendingProduct) {
      setPendingProduct(externalPendingProduct);
      setPendingQuantity(1);
      // In purchase mode, leave purchase price empty for user to enter
      // In standard mode, pre-fill with sale price
      setPendingPrice(showSalePriceColumn ? '' : (externalPendingProduct.SalePrice || externalPendingProduct.Price || ''));
      setPendingError('');
      if (onExternalPendingConsumed) onExternalPendingConsumed();
    }
  }, [externalPendingProduct, showSalePriceColumn, onExternalPendingConsumed]);
  
  const handleAddProduct = () => {
    if (!onProductAdd || !pendingProduct) return;

    // Validate price > 0 (for all products including temp)
    if (!pendingPrice || parseFloat(pendingPrice) <= 0) {
      setPendingError(t('please_enter_valid_price') || 'Please enter a valid price greater than 0');
      return;
    }
    
    // Validate quantity > 0 - handle both empty string and zero
    const qty = pendingQuantity === '' ? 0 : parseInt(pendingQuantity, 10);
    if (!qty || qty <= 0) {
      setPendingError(t('please_enter_valid_quantity') || 'Please enter a valid quantity greater than 0');
      return;
    }

    setPendingError('');
    
    // Call onProductAdd - parent component handles duplicate checking
    onProductAdd(pendingProduct, qty, pendingPrice);
    
    setPendingProduct(null);
    setPendingQuantity(1);
    setPendingPrice('');
    // Force ProductSelect to remount and get focus
    setSelectKey(prev => prev + 1);
    // Focus on product select after a short delay
    setTimeout(() => {
      if (productSelectRef.current) {
        productSelectRef.current.focus();
      }
    }, 100);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'visible',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
              {convertToInvoiceMode && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5,
                  textAlign: 'center',
                  width: '60px'
                }}>
                  <Checkbox
                    checked={selectedLineItems.length === products.length && products.length > 0}
                    onChange={(e) => onLineItemSelectAll && onLineItemSelectAll(e.target.checked)}
                    sx={{ 
                      color: 'white',
                      '&.Mui-checked': { color: 'white' }
                    }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5,
                minWidth: '180px'
              }}>
                {t("product_name") || "PRODUCT NAME"}
              </TableCell>
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5, 
                textAlign: 'center', 
                width: '120px' 
              }}>
                {t("quantity") || "QUANTITY"}
              </TableCell>
              {showSalePriceColumn ? (
                // Purchase Order Mode: Show Cost Price, Purchase Price and Sale Price
                <>
                  {showCostPriceColumn && (
                    <TableCell sx={{ 
                      color: 'white', 
                      fontWeight: 700, 
                      textTransform: 'uppercase', 
                      fontSize: '0.75rem', 
                      letterSpacing: '0.5px', 
                      py: 1.5, 
                      textAlign: 'right', 
                      width: '130px'
                    }}>
                      {t("cost_price") || "COST PRICE"}
                    </TableCell>
                  )}
                  <TableCell sx={{ 
                    color: 'white', 
                    fontWeight: 700, 
                    textTransform: 'uppercase', 
                    fontSize: '0.75rem', 
                    letterSpacing: '0.5px', 
                    py: 1.5, 
                    textAlign: 'right', 
                    width: '130px'
                  }}>
                    {t("purchase_price") || "PURCHASE PRICE"}
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'white', 
                    fontWeight: 700, 
                    textTransform: 'uppercase', 
                    fontSize: '0.75rem', 
                    letterSpacing: '0.5px', 
                    py: 1.5, 
                    textAlign: 'right', 
                    width: '130px'
                  }}>
                    {t("sale_price") || "SALE PRICE"}
                  </TableCell>
                </>
              ) : (
                // Standard Mode: Show Price
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '120px' 
                }}>
                  {isTvaIncluded 
                    ? (t("price_incl_vat") || "PRICE (INCL. VAT)")
                    : (t("price_excl_vat") || "PRICE (EXCL. VAT)")
                  }
                </TableCell>
              )}
              {showVatColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '100px' 
                }}>
                  {t("vat") || "VAT"} ({vatPercentage || 0}%)
                </TableCell>
              )}
              {showTotalExclColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '130px' 
                }}>
                  {t("total_excl") || "TOTAL EXCL."}
                </TableCell>
              )}
              {showTotalInclColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '130px' 
                }}>
                  {t("total_incl") || "TOTAL INCL."}
                </TableCell>
              )}
              {showProfit && (
                <>
                  <TableCell sx={{ 
                    color: 'white', 
                    fontWeight: 700, 
                    textTransform: 'uppercase', 
                    fontSize: '0.75rem', 
                    letterSpacing: '0.5px', 
                    py: 1.5, 
                    textAlign: 'right', 
                    width: '120px'
                  }}>
                    {t("cost_price") || "COST PRICE"}
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'white', 
                    fontWeight: 700, 
                    textTransform: 'uppercase', 
                    fontSize: '0.75rem', 
                    letterSpacing: '0.5px', 
                    py: 1.5, 
                    textAlign: 'right', 
                    width: '120px'
                  }}>
                    {t("profit") || "PROFIT"}
                  </TableCell>
                </>
              )}
              {showDiscountColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'center', 
                  width: '100px'
                }}>
                  {t("discount") || "DISCOUNT"}
                </TableCell>
              )}
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5, 
                textAlign: 'center', 
                width: '100px' 
              }}>
                {t("actions") || "ACTIONS"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Added Products Rows */}
            {products.map((p) => {
              const vatTotal = parseFloat(p.VATAmount || 0).toFixed(2);
              const totalExcl = parseFloat(p.TotalExcl || 0);
              const totalIncl = parseFloat(p.TotalIncl || 0);
              const isSelected = selectedLineItems.includes(p._rowKey || p.Id);
              
              return (
                <TableRow 
                  key={p._rowKey || p.Id}
                  sx={{
                    '&:hover': { bgcolor: '#f8f9fa' },
                    borderBottom: '1px solid #e0e0e0',
                    bgcolor: convertToInvoiceMode && isSelected ? alpha('#667eea', 0.08) : 'transparent'
                  }}
                >
                  {convertToInvoiceMode && (
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onLineItemToggle && onLineItemToggle(p._rowKey || p.Id)}
                        sx={{
                          '&.Mui-checked': { color: '#667eea' }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ py: 1.5, fontSize: '0.85rem', color: '#333', minWidth: '180px' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        {showSalePriceColumn && onProductEdit && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onProductEdit(p)}
                            sx={{ 
                              p: 0.3,
                              flexShrink: 0,
                              '&:hover': { bgcolor: alpha('#2196f3', 0.1) }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Tooltip title={p.Name || p.ProductName || p.productName} placement="top" arrow>
                          <Typography variant="body2" fontWeight={500} sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {p.Name || p.ProductName || p.productName}
                          </Typography>
                        </Tooltip>
                      </Box>
                      {p.SerialNumber && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          SN: {p.SerialNumber}
                        </Typography>
                      )}
                      {(p.SKU || p.sku || p.ProductSKU || p.SKUBarCode) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          SKU: {p.SKU || p.sku || p.ProductSKU || p.SKUBarCode}
                        </Typography>
                      )}
                      {(p.isTemp || (!showSalePriceColumn && (p.MainStoreStock !== undefined || p.VirtualStoreStock !== undefined))) && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {p.isTemp && (
                            <Chip 
                              label={t('temp') || 'Temp'} 
                              size="small" 
                              color="warning"
                              sx={{ height: '20px', fontSize: '0.7rem', fontWeight: 600 }} 
                            />
                          )}
                          {!showSalePriceColumn && p.MainStoreStock !== undefined && (
                            <Chip 
                              label={p.MainStoreStock}
                              size="small"
                              sx={{ height: '20px', fontSize: '0.7rem', fontWeight: 600, bgcolor: '#4caf50', color: 'white' }}
                            />
                          )}
                          {!showSalePriceColumn && p.VirtualStoreStock !== undefined && (
                            <Chip 
                              label={p.VirtualStoreStock}
                              size="small"
                              sx={{ height: '20px', fontSize: '0.7rem', fontWeight: 600, bgcolor: '#ff9800', color: 'white' }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                    <Tooltip 
                      title={p._validationErrors?.quantity ? (t('please_enter_valid_quantity') || 'Please enter a valid quantity (greater than 0)') : ''}
                      open={p._validationErrors?.quantity ? undefined : false}
                      arrow
                    >
                      <TextField
                        value={p.Quantity}
                        onFocus={(e) => {
                          // Store original value when field is focused
                          originalValuesRef.current[`${p._rowKey}_quantity`] = e.target.value;
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty string or valid positive integers
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value, 10);
                            onQuantityChange && onQuantityChange(p, numValue || 0);
                          }
                        }}
                        onBlur={(e) => {
                          const originalValue = originalValuesRef.current[`${p._rowKey}_quantity`];
                          const currentValue = e.target.value;
                          
                          // On blur, if empty or 0, set to 1
                          const numValue = parseInt(currentValue, 10);
                          if (!numValue || numValue <= 0) {
                            // Only call if value actually changed
                            if (originalValue !== '1') {
                              onQuantityChange && onQuantityChange(p, 1);
                            }
                          } else if (originalValue !== currentValue) {
                            // Value changed, the onChange already handled it, no need to call again
                            // Just clean up the ref
                          }
                          
                          // Clean up
                          delete originalValuesRef.current[`${p._rowKey}_quantity`];
                        }}
                        size="small"
                        disabled={convertToInvoiceMode}
                        error={p._validationErrors?.quantity}
                        sx={{
                          width: '80px',
                          '& .MuiOutlinedInput-root': {
                            '& input': { textAlign: 'center', py: 0.5 },
                            ...(p._validationErrors?.quantity && {
                              '& fieldset': { borderColor: '#d32f2f', borderWidth: '2px' }
                            })
                          }
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  {showSalePriceColumn ? (
                    // Purchase Order Mode: Show Cost Price, Purchase Price and Sale Price columns
                    <>
                      {/* Cost Price Column - Display Only */}
                      {showCostPriceColumn && (
                        <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600, 
                              color: '#9c27b0'
                            }}
                          >
                            €{(p.CostPrice || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                      )}
                      {/* Purchase Price Column (Unit Cost) - Editable */}
                      <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                        <Tooltip 
                          title={p._validationErrors?.price ? (t('please_enter_valid_price') || 'Please enter a valid price (greater than 0)') : ''}
                          open={p._validationErrors?.price ? undefined : false}
                          arrow
                        >
                          <TextField
                            value={p.Price}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                onPriceChange && onPriceChange(p, value === '' ? 0 : value);
                              }
                            }}
                            onBlur={(e) => {
                              // Format to 2 decimal places on blur
                              const numValue = parseFloat(e.target.value) || 0;
                              onPriceChange && onPriceChange(p, parseFloat(numValue.toFixed(2)));
                              // Save to database
                              onPriceBlur && onPriceBlur(p, parseFloat(numValue.toFixed(2)));
                            }}
                            size="small"
                            disabled={convertToInvoiceMode}
                            error={p._validationErrors?.price}
                            sx={{
                              width: '100px',
                              '& .MuiOutlinedInput-root': {
                                '& input': { textAlign: 'right', py: 0.5, fontWeight: 500 },
                                ...(p._validationErrors?.price && {
                                  '& fieldset': { borderColor: '#d32f2f', borderWidth: '2px' }
                                })
                              }
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      {/* Sale Price Column - Display Only */}
                      <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600, 
                              color: p.SalePrice > 0 && p.Price > p.SalePrice ? 'warning.main' : 'primary.main'
                            }}
                          >
                            €{(p.SalePrice || 0).toFixed(2)}
                          </Typography>
                          {p.SalePrice > 0 && p.Price > p.SalePrice && (
                            <Tooltip title="Purchase price is higher than sale price.">
                              <WarningIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </>
                  ) : (
                    // Standard Mode: Show Price column only
                    <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                      <Tooltip 
                        title={p._validationErrors?.price ? (t('please_enter_valid_price') || 'Please enter a valid price (greater than 0)') : ''}
                        open={p._validationErrors?.price ? undefined : false}
                        arrow
                      >
                        <TextField
                          value={p.Price}
                          onFocus={(e) => {
                            // Store original value when field is focused
                            originalValuesRef.current[`${p._rowKey}_price`] = e.target.value;
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string, numbers with optional decimal point
                            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                              onPriceChange && onPriceChange(p, value);
                            }
                          }}
                          onBlur={(e) => {
                            const originalValue = originalValuesRef.current[`${p._rowKey}_price`];
                            const numValue = parseFloat(e.target.value);
                            const originalNumValue = parseFloat(originalValue);
                            
                            // Format to 2 decimal places on blur, ensure positive value
                            if (isNaN(numValue) || numValue <= 0) {
                              // If invalid, keep the original price or set to 0
                              if (originalValue !== '0' && originalValue !== '') {
                                onPriceChange && onPriceChange(p, 0);
                              }
                            } else {
                              const formattedValue = parseFloat(numValue.toFixed(2));
                              // Only save to database if value actually changed
                              if (formattedValue !== originalNumValue) {
                                onPriceChange && onPriceChange(p, formattedValue);
                                // Save to database
                                onPriceBlur && onPriceBlur(p, formattedValue);
                              }
                            }
                            
                            // Clean up
                            delete originalValuesRef.current[`${p._rowKey}_price`];
                          }}
                          size="small"
                          disabled={convertToInvoiceMode}
                          error={p._validationErrors?.price}
                          sx={{
                            width: '100px',
                            '& .MuiOutlinedInput-root': {
                              '& input': { textAlign: 'right', py: 0.5, fontWeight: 500 },
                              ...(p._validationErrors?.price && {
                                '& fieldset': { borderColor: '#d32f2f', borderWidth: '2px' }
                              })
                            }
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                  )}
                  {showVatColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 500, color: '#f57c00' }}>
                      €{vatTotal}
                    </TableCell>
                  )}
                  {showTotalExclColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 600, color: '#388e3c' }}>
                      €{totalExcl.toFixed(2)}
                    </TableCell>
                  )}
                  {showTotalInclColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 600, color: '#388e3c' }}>
                      €{totalIncl.toFixed(2)}
                    </TableCell>
                  )}
                  {showProfit && (
                    <>
                      <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 600, color: '#e65100' }}>
                        {(() => {
                          const costPrice = Number(p.CostPrice || p.BuyPrice || 0);
                          return `€${costPrice.toFixed(2)}`;
                        })()}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 600, color: '#2e7d32' }}>
                        {(() => {
                          const costPrice = Number(p.CostPrice || 0);
                          const quantity = Number(p.Quantity || 0);
                          const totalCost = costPrice * quantity;
                          const profit = totalExcl - totalCost;
                          return `€${profit.toFixed(2)}`;
                        })()}
                      </TableCell>
                    </>
                  )}
                  {showDiscountColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      <Checkbox
                        checked={p.applyDiscount !== false}
                        onChange={() => onDiscountToggle && onDiscountToggle(p)}
                        disabled={convertToInvoiceMode}
                        sx={{
                          color: '#ff9800',
                          '&.Mui-checked': { color: '#ff9800' },
                          '&.Mui-disabled': { color: '#ccc' }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {onSerialNumberClick && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onSerialNumberClick(p)}
                          disabled={convertToInvoiceMode}
                          sx={{ minWidth: 36, px: 1, py: 0.25, fontSize: '0.7rem', fontWeight: 700, borderRadius: '6px' }}
                        >
                          SN
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => onProductRemove && onProductRemove(p)}
                        disabled={convertToInvoiceMode}
                        sx={{
                          color: '#d32f2f',
                          '&:hover': { bgcolor: alpha('#d32f2f', 0.1) },
                          '&:disabled': { color: '#ccc' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            
            {/* Product Search Row - For adding new products */}
            {showAddProductRow && !convertToInvoiceMode && (
              <TableRow sx={{ bgcolor: '#fffef0', borderTop: '2px solid #667eea' }}>
                {!pendingProduct ? (
                  <TableCell colSpan={
                    2 + // Product name + Quantity (always present)
                    (showSalePriceColumn ? (showCostPriceColumn ? 3 : 2) : 1) + // Cost price + Purchase price + Sale price OR just Price
                    (showVatColumn ? 1 : 0) +
                    (showTotalExclColumn ? 1 : 0) +
                    (showTotalInclColumn ? 1 : 0) +
                    (showProfit ? 2 : 0) +
                    (showDiscountColumn ? 1 : 0) +
                    1 // Actions (always present)
                  } sx={{ py: 1.5, position: 'relative', zIndex: 10, bgcolor: '#fffef0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {onAddProductClick && (
                        <Tooltip title="Add New Product" placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={onAddProductClick}
                            sx={{
                              color: '#667eea',
                              bgcolor: alpha('#667eea', 0.1),
                              '&:hover': { bgcolor: alpha('#667eea', 0.2) },
                              flexShrink: 0
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Box sx={{ flex: 1, position: 'relative', zIndex: 20 }}>
                        <ProductSelect
                          ref={productSelectRef}
                          key={`${selectKey}-${productSearchKey ?? ''}`}
                          value={null}
                          formatOptionLabel={(option) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <span>{option.label}</span>
                              {option.MainStoreStock !== undefined && (
                                <Chip 
                                  label={option.MainStoreStock}
                                  size="small"
                                  sx={{ 
                                    height: '20px', 
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: '#4caf50',
                                    color: 'white'
                                  }}
                                />
                              )}
                              {option.VirtualStoreStock !== undefined && (
                                <Chip 
                                  label={option.VirtualStoreStock}
                                  size="small"
                                  sx={{ 
                                    height: '20px', 
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: '#ff9800',
                                    color: 'white'
                                  }}
                                />
                              )}
                            </Box>
                          )}
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              // Handle custom options
                              if (selectedOption.isCustomOption) {
                                if (selectedOption.value === '__add_new__') {
                                  // Open add product dialog immediately
                                  if (onCustomProductOptionSelect) {
                                    onCustomProductOptionSelect('add_new', selectedOption.searchText);
                                  }
                                  return;
                                } else if (selectedOption.value === '__temp_add__' && selectedOption.isTempProduct) {
                                  // Set as pending product with searchText as name
                                  setPendingProduct({
                                    Id: `temp_${Date.now()}`,
                                    Name: selectedOption.searchText,
                                    ProductName: selectedOption.searchText,
                                    Price: 0,
                                    SalePrice: 0,
                                    isTemp: true,
                                    searchText: selectedOption.searchText,
                                  });
                                  setPendingQuantity(1);
                                  setPendingPrice(0);
                                  return;
                                }
                              }
                              
                              // Regular product selection
                              const productPrice = selectedOption.SalePrice || selectedOption.Price || selectedOption.price || "";
                              setPendingProduct(selectedOption);
                              setPendingQuantity(1);
                              // In purchase mode, leave purchase price empty for user to enter
                              // In standard mode, pre-fill with sale price
                              setPendingPrice(showSalePriceColumn ? '' : productPrice);
                            }
                          }}
                          loadOptions={loadProductOptions}
                          placeholder={t("search_products") || "Search and add products..."}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            menu: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({ ...base, width: '100%' })
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                ) : (
                  <>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {pendingProduct.Name || pendingProduct.productName}
                          </Typography>
                          {(pendingProduct.SKU || pendingProduct.sku || pendingProduct.ProductSKU || pendingProduct.SKUBarCode) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              SKU: {pendingProduct.SKU || pendingProduct.sku || pendingProduct.ProductSKU || pendingProduct.SKUBarCode}
                            </Typography>
                          )}
                        </Box>
                        {pendingProduct.isTemp && (
                          <Chip 
                            label={t('temp') || 'Temp'} 
                            size="small" 
                            color="warning"
                            sx={{ 
                              height: '20px', 
                              fontSize: '0.7rem',
                              fontWeight: 600
                            }} 
                          />
                        )}
                        {!showSalePriceColumn && pendingProduct.MainStoreStock !== undefined && (
                          <Chip 
                            label={pendingProduct.MainStoreStock}
                            size="small"
                            sx={{ 
                              height: '20px', 
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: '#4caf50',
                              color: 'white'
                            }}
                          />
                        )}
                        {!showSalePriceColumn && pendingProduct.VirtualStoreStock !== undefined && (
                          <Chip 
                            label={pendingProduct.VirtualStoreStock}
                            size="small"
                            sx={{ 
                              height: '20px', 
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: '#ff9800',
                              color: 'white'
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      <TextField
                        value={pendingQuantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty string or valid positive integers
                          if (value === '' || /^\d*$/.test(value)) {
                            setPendingQuantity(value === '' ? '' : parseInt(value, 10));
                            if (pendingError) setPendingError('');
                          }
                        }}
                        onBlur={(e) => {
                          // On blur, if empty or 0, set to 1
                          const value = e.target.value;
                          if (value === '' || parseInt(value, 10) <= 0) {
                            setPendingQuantity(1);
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddProduct();
                          }
                        }}
                        size="small"
                        autoFocus={!pendingProduct?.isTemp}
                        error={pendingError && pendingError.includes('quantity')}
                        sx={{
                          width: '80px',
                          '& .MuiOutlinedInput-root': {
                            '& input': { textAlign: 'center', py: 0.5 }
                          }
                        }}
                      />
                    </TableCell>
                    {showSalePriceColumn ? (
                      // Purchase Order Mode: Show Cost Price, Purchase Price and Sale Price columns
                      <>
                        {/* Cost Price Column (display only) */}
                        {showCostPriceColumn && (
                          <TableCell sx={{ py: 1.5, textAlign: 'right', bgcolor: '#fffef0' }}>
                            <Typography variant="body2" color="text.secondary">
                              €{(pendingProduct?.CostPrice || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                        )}
                        {/* Purchase Price Column (editable) */}
                        <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                          <TextField
                            value={pendingPrice}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setPendingPrice(value);
                                if (pendingError) setPendingError('');
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddProduct();
                              }
                            }}
                            size="small"
                            autoFocus
                            error={!!pendingError && !pendingError.includes('quantity')}
                            placeholder="0.00"
                            sx={{
                              width: '100px',
                              '& .MuiOutlinedInput-root': {
                                '& input': { textAlign: 'right', py: 0.5, fontWeight: 500 },
                                ...(pendingError && !pendingError.includes('quantity') && {
                                  '& fieldset': { borderColor: '#d32f2f', borderWidth: '2px' }
                                })
                              }
                            }}
                          />
                        </TableCell>
                        {/* Sale Price Column (display only) */}
                        <TableCell sx={{ py: 1.5, textAlign: 'right', bgcolor: '#fffef0' }}>
                          <Typography variant="body2" color="text.secondary">
                            €{(pendingProduct?.SalePrice || pendingProduct?.Price || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                      </>
                    ) : (
                      // Standard Mode: Show Price column (editable)
                      <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                        <TextField
                          value={pendingPrice}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setPendingPrice(value);
                              if (pendingError) setPendingError('');
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddProduct();
                            }
                          }}
                          size="small"
                          autoFocus={pendingProduct?.isTemp}
                          error={!!pendingError && !pendingError.includes('quantity')}
                          placeholder={pendingProduct?.isTemp ? '0.00' : ''}
                          sx={{
                            width: '100px',
                            '& .MuiOutlinedInput-root': {
                              '& input': { textAlign: 'right', py: 0.5, fontWeight: 500 },
                              ...(pendingError && !pendingError.includes('quantity') && {
                                '& fieldset': { borderColor: '#d32f2f', borderWidth: '2px' }
                              })
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    {showVatColumn && <TableCell sx={{ py: 1.5, bgcolor: '#fffef0' }}></TableCell>}
                    {showTotalExclColumn && <TableCell sx={{ py: 1.5, bgcolor: '#fffef0' }}></TableCell>}
                    {showTotalInclColumn && <TableCell sx={{ py: 1.5, bgcolor: '#fffef0' }}></TableCell>}
                    {showProfit && (
                      <>
                        <TableCell sx={{ py: 1.5, bgcolor: '#fffef0' }}></TableCell>
                        <TableCell sx={{ py: 1.5, bgcolor: '#fffef0' }}></TableCell>
                      </>
                    )}
                    {showDiscountColumn && <TableCell sx={{ py: 1.5, bgcolor: '#fffef0' }}></TableCell>}
                    <TableCell sx={{ py: 1.5, textAlign: 'center', bgcolor: '#fffef0' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={handleAddProduct}
                          sx={{
                            color: '#4caf50',
                            bgcolor: alpha('#4caf50', 0.1),
                            '&:hover': { bgcolor: alpha('#4caf50', 0.2) }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPendingProduct(null);
                            setPendingQuantity(1);
                            setPendingPrice('');
                            setPendingError('');
                          }}
                          sx={{
                            color: '#d32f2f',
                            bgcolor: alpha('#d32f2f', 0.1),
                            '&:hover': { bgcolor: alpha('#d32f2f', 0.2) }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {/* Inline validation error below buttons */}
                      {pendingError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, textAlign: 'center', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                          {pendingError}
                        </Typography>
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>
            )}
            
            {/* Totals Row - At the bottom */}
            {products.length > 0 && (
              <TableRow sx={{ bgcolor: '#f0f0f0', borderTop: '2px solid #667eea' }}>
                {convertToInvoiceMode && (
                  <TableCell sx={{ py: 1.5 }}></TableCell>
                )}
                <TableCell sx={{ py: 1.5, fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                  {t("total") || "TOTAL"}
                </TableCell>
                <TableCell sx={{ py: 1.5, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                  {products.reduce((sum, p) => sum + parseFloat(p.Quantity || 0), 0)}
                </TableCell>
                {showSalePriceColumn ? (
                  // Purchase Order Mode: Empty cells for Cost Price, Purchase Price and Sale Price columns
                  <>
                    {showCostPriceColumn && <TableCell sx={{ py: 1.5 }}></TableCell>}
                    <TableCell sx={{ py: 1.5 }}></TableCell>
                    <TableCell sx={{ py: 1.5 }}></TableCell>
                  </>
                ) : (
                  // Standard Mode: One empty cell for Price column
                  <TableCell sx={{ py: 1.5 }}></TableCell>
                )}
                {showVatColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#f57c00' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.VATAmount || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                {showTotalExclColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#388e3c' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.TotalExcl || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                {showTotalInclColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.95rem', color: '#667eea' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.TotalIncl || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                {showProfit && (
                  <>
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#e65100' }}>
                      €{products.reduce((sum, p) => {
                        const costPrice = Number(p.CostPrice || p.BuyPrice || 0);
                        const quantity = Number(p.Quantity || 0);
                        return sum + (costPrice * quantity);
                      }, 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#2e7d32' }}>
                      €{products.reduce((sum, p) => {
                        const costPrice = Number(p.CostPrice || p.BuyPrice || 0);
                        const quantity = Number(p.Quantity || 0);
                        const totalCost = costPrice * quantity;
                        const totalExcl = parseFloat(p.TotalExcl || 0);
                        return sum + (totalExcl - totalCost);
                      }, 0).toFixed(2)}
                    </TableCell>
                  </>
                )}
                {showDiscountColumn && <TableCell sx={{ py: 1.5 }}></TableCell>}
                <TableCell sx={{ py: 1.5 }}></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default DocumentProductsTable;
